/**
 * Antra `.banner-process-area` pattern (see assets/js/banner-process.js on their site):
 * — tiny-slider shows 4 caption columns (like Swiper slidesPerView: 4)
 * — separate stack of 4 full-bleed images; hover/focus a column → show that image
 * Reference: https://antra.ibthemespro.com/index-2.html
 */
export function initImgPartsSlider(loco) {
  const section = document.querySelector(".image-slider");
  const container = section?.querySelector("[data-image-slider]");
  const stack = section?.querySelector(".image-slider__bg-stack");
  const tnsFactory = window.tns;

  if (!section || !container || !stack || !tnsFactory) return;

  const layers = [...stack.querySelectorAll(".image-slider__bg")];
  if (!layers.length) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const bumpLoco = () => loco?.bump?.();

  const instance = tnsFactory({
    container,
    items: 4,
    slideBy: 1,
    gutter: 24,
    edgePadding: 0,
    controls: false,
    nav: false,
    mouseDrag: true,
    loop: true,
    speed: reduceMotion ? 0 : 480,
    responsive: {
      0: { items: 1, gutter: 0 },
      576: { items: 2, gutter: 16 },
      992: { items: 4, gutter: 24 },
    },
  });

  if (!instance) return;

  let active = 0;
  let running = false;
  let pendingTransition = null;

  const setCaptionActive = (index) => {
    section.querySelectorAll(".image-slider__column").forEach((col) => {
      const i = parseInt(col.getAttribute("data-index"), 10);
      const cap = col.querySelector(".image-slider__caption");
      if (!cap || Number.isNaN(i)) return;
      cap.classList.toggle("is-active", i === index);
    });
  };

  const resetLayerStyle = (el) => {
    el.style.transition = "";
    el.style.clipPath = "";
    el.style.opacity = "";
    el.style.visibility = "";
    el.style.zIndex = "";
  };

  const settleLayers = (index) => {
    layers.forEach((layer, i) => {
      resetLayerStyle(layer);
      layer.classList.toggle("is-active", i === index);
    });
  };

  const abortTransition = () => {
    if (!pendingTransition) return;
    pendingTransition.abort();
    pendingTransition = null;
    running = false;
    settleLayers(active);
  };

  const activateLayer = (index) => {
    if (index < 0 || index >= layers.length) return;
    if (index === active && !running) return;

    if (running) abortTransition();
    if (index === active) return;

    setCaptionActive(index);

    const from = active;
    const to = index;
    const prev = layers[from];
    const next = layers[to];

    if (reduceMotion) {
      active = to;
      settleLayers(active);
      return;
    }

    running = true;

    layers.forEach((layer, i) => {
      if (i !== from && i !== to) {
        layer.classList.remove("is-active");
        resetLayerStyle(layer);
      }
    });

    // Wipe uses inline visibility only — never two `.is-active` bg layers at once.
    prev.classList.remove("is-active");
    prev.style.visibility = "visible";
    prev.style.opacity = "1";
    prev.style.zIndex = "1";

    next.classList.remove("is-active");
    next.style.visibility = "visible";
    next.style.opacity = "1";
    next.style.zIndex = "2";
    next.style.clipPath = "inset(0 0 100% 0)";
    next.style.transition = "clip-path 0.7s cubic-bezier(0.22, 1, 0.36, 1)";

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        next.style.clipPath = "inset(0 0 0 0)";
      });
    });

    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      active = to;
      running = false;
      pendingTransition = null;
      settleLayers(active);
    };

    const onEnd = (e) => {
      if (e.propertyName !== "clip-path") return;
      finish();
    };

    const fallbackTimer = window.setTimeout(finish, 900);

    pendingTransition = {
      abort: () => {
        if (settled) return;
        settled = true;
        next.removeEventListener("transitionend", onEnd);
        window.clearTimeout(fallbackTimer);
      },
    };

    next.addEventListener("transitionend", onEnd);
  };

  const onColumnInteract = (col) => {
    const raw = col.getAttribute("data-index");
    const i = raw == null ? NaN : parseInt(raw, 10);
    if (Number.isNaN(i)) return;
    const idx = ((i % layers.length) + layers.length) % layers.length;
    activateLayer(idx);
  };

  section.querySelectorAll(".image-slider__column").forEach((col) => {
    col.addEventListener("mouseenter", () => onColumnInteract(col));
    col.addEventListener("focusin", () => onColumnInteract(col));
    col.addEventListener(
      "click",
      () => {
        if (window.matchMedia("(hover: none)").matches) onColumnInteract(col);
      },
      { passive: true },
    );
  });

  layers.forEach((el, i) => {
    if (el.classList.contains("is-active")) active = i;
  });

  setCaptionActive(active);
  settleLayers(active);

  instance.events.on("transitionEnd", bumpLoco);
  instance.events.on("indexChanged", bumpLoco);
  bumpLoco();
}
